import { useEffect, useState } from 'react';
import { TrendingUp, Clock, AlertTriangle } from 'lucide-react';

function AnimatedCounter({ value, duration = 600 }) {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (value === 0) { setDisplay(0); return; }
        let start = 0;
        const step = Math.max(1, Math.ceil(value / (duration / 16)));
        const timer = setInterval(() => {
            start += step;
            if (start >= value) { setDisplay(value); clearInterval(timer); }
            else setDisplay(start);
        }, 16);
        return () => clearInterval(timer);
    }, [value, duration]);
    return <span>{display.toLocaleString()}</span>;
}

export default function WorklistKPIBar({ items = [], statuses = [], pendingCount = 0, totalOverall = 0, pendingOverall = 0, highPriorityOverall = 0, pageTotal = 0 }) {
    // Use overall counts when available, fallback to page-level counts
    const total = totalOverall || items.length;
    const pageCount = pageTotal || items.length;

    // Derive "high priority" count from current page items as a fallback
    const urgentStatuses = new Set(['Pending Review', 'Overdue', 'Escalated', 'Urgent', 'High Priority']);
    const highPriorityPage = items.filter(item => {
        const st = item.resolved?.__platform_status__ || item.field_overrides?.__platform_status__ || '';
        return urgentStatuses.has(st);
    }).length;

    const highPriority = highPriorityOverall > 0 ? highPriorityOverall : highPriorityPage;
    const pending = pendingOverall > 0 ? pendingOverall : pendingCount;

    if (total === 0 && items.length === 0) return null;

    const cards = [
        {
            label: 'TOTAL CLAIMS',
            value: total,
            icon: TrendingUp,
            iconColor: 'text-emerald-500',
            iconBg: 'bg-emerald-50 dark:bg-emerald-900/20',
            subtitle: total !== pageCount ? `${pageCount} on this page` : null,
        },
        {
            label: 'PENDING SYNC',
            value: pending,
            icon: Clock,
            iconColor: 'text-amber-500',
            iconBg: 'bg-amber-50 dark:bg-amber-900/20',
            subtitle: null,
        },
        {
            label: 'HIGH PRIORITY',
            value: highPriority,
            icon: AlertTriangle,
            iconColor: 'text-red-500',
            iconBg: 'bg-red-50 dark:bg-red-900/20',
            subtitle: null,
        },
    ];

    return (
        <div className="flex gap-4">
            {cards.map((card) => {
                const Icon = card.icon;
                return (
                    <div
                        key={card.label}
                        className="flex-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700/80 rounded-2xl px-5 py-4 flex items-start justify-between transition-all hover:shadow-md hover:border-gray-300 dark:hover:border-slate-600"
                    >
                        <div>
                            <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1">
                                {card.label}
                            </p>
                            <p className="text-3xl font-black text-gray-900 dark:text-slate-100 leading-none tabular-nums">
                                <AnimatedCounter value={card.value} />
                            </p>
                            {card.label === 'TOTAL CLAIMS' && total > 0 && (
                                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold mt-1.5 flex items-center gap-1">
                                    <TrendingUp className="w-3 h-3" />
                                    {card.subtitle || `${total} total items`}
                                </p>
                            )}
                        </div>
                        <div className={`p-2 rounded-xl ${card.iconBg}`}>
                            <Icon className={`w-5 h-5 ${card.iconColor}`} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
